import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { StatusBadge } from '../../../components/ui/Badge.js';
import { Card } from '../../../components/ui/Card.js';
import { type Column, DataTable } from '../../../components/ui/DataTable.js';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { api } from '../../../services/api.js';
import { RESOURCE_KEYS } from '../../../services/resourceKeys.js';
import {
  type Approval,
  ApprovalBadge,
  type ApprovalHistoryEntry,
  ApprovalPanel,
  type DecisionFor,
} from '../../approvals/approval.js';

interface Redemption {
  _id: string;
  booking_id: string;
  /** 358: a use by a booking, or credit given back when that booking was cancelled before pickup. Pre-358 rows have
   *  none and were uses. */
  kind?: 'redeem' | 'return';
  amount_minor: number;
  balance_after_minor: number;
  redeemed_by?: string;
  redeemed_at: string;
}

interface GiftCertificateDetail {
  _id: string;
  code: string;
  initial_value_minor: number;
  balance_minor: number;
  status: 'active' | 'depleted' | 'revoked';
  expires_at?: string;
  recipient_name?: string;
  recipient_email?: string;
  note?: string;
  redemptions: Redemption[];
  created_at: string;
  updated_at: string;
  /** 372: absent before #1240 (approved). */
  approval?: Approval;
  /** 372: what THIS viewer may do about it (the server decides). */
  decision?: DecisionFor;
  approval_history?: ApprovalHistoryEntry[];
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMoney(minor: number) {
  return `RM ${(minor / 100).toFixed(2)}`;
}

export function GiftCertificateDetailView({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: RESOURCE_KEYS.giftCertificate.one(id as string),
    queryFn: () => api.get<GiftCertificateDetail>(`/gift-certificates/${id}`),
  });
  const qc = useQueryClient();
  // 372 (#1240): approve, or reject with a reason.
  const decide = useMutation({
    mutationFn: (d: { path: 'approve' | 'reject'; reason?: string }) =>
      api.post(`/gift-certificates/${id}/${d.path}`, d.reason ? { reason: d.reason } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: RESOURCE_KEYS.giftCertificate.one(id as string) });
      qc.invalidateQueries({ queryKey: RESOURCE_KEYS.giftCertificate.list });
    },
  });

  const columns: Column<Redemption>[] = [
    {
      key: 'kind',
      header: 'Type',
      render: (r) => (
        <span data-testid="ledger-kind">{r.kind === 'return' ? 'Returned' : 'Used'}</span>
      ),
    },
    {
      key: 'booking_id',
      header: 'Booking',
      render: (r) => (
        <Link
          data-testid="ledger-booking"
          to={`/bookings/${r.booking_id}`}
          className="text-primary hover:underline"
        >
          {r.booking_id}
        </Link>
      ),
    },
    {
      key: 'amount_minor',
      header: 'Amount',
      render: (r) => formatMoney(r.amount_minor),
    },
    {
      key: 'balance_after_minor',
      header: 'Balance After',
      render: (r) => formatMoney(r.balance_after_minor),
    },
    {
      key: 'redeemed_at',
      header: 'Redeemed At',
      render: (r) => formatDateTime(r.redeemed_at),
    },
  ];

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="Gift Certificate" description="Loading…" fallback="/gift-certificates" />
        <DataTable columns={columns} data={[]} loading={isLoading} keyExtractor={(r) => r._id} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={data.code}
        description="Gift certificate details and redemption history"
        fallback="/gift-certificates"
      />
      <div className="max-w-3xl space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Summary</h2>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-steel">Status</dt>
              <dd className="mt-0.5">
                {/* 372: waiting for approval, or rejected, is said first (as in the list); 401: unless revoked. */}
                {data.status !== 'revoked' &&
                data.approval &&
                data.approval.status !== 'approved' ? (
                  <ApprovalBadge approval={data.approval} />
                ) : (
                  <StatusBadge status={data.status} />
                )}
              </dd>
            </div>
            <div>
              <dt className="text-steel">Code</dt>
              <dd className="mt-0.5 text-ink">{data.code}</dd>
            </div>
            <div>
              <dt className="text-steel">Initial Value</dt>
              <dd className="mt-0.5 text-ink">{formatMoney(data.initial_value_minor)}</dd>
            </div>
            <div>
              <dt className="text-steel">Balance</dt>
              <dd className="mt-0.5 text-ink">{formatMoney(data.balance_minor)}</dd>
            </div>
            <div>
              <dt className="text-steel">Expires</dt>
              <dd className="mt-0.5 text-ink">{formatDate(data.expires_at)}</dd>
            </div>
            <div>
              <dt className="text-steel">Issued</dt>
              <dd className="mt-0.5 text-ink">{formatDate(data.created_at)}</dd>
            </div>
            {/* 365: no longer asked for; certificates issued before keep and show theirs. */}
            {data.recipient_name && (
              <div>
                <dt className="text-steel">Recipient Name</dt>
                <dd className="mt-0.5 text-ink">{data.recipient_name}</dd>
              </div>
            )}
            <div>
              <dt className="text-steel">Recipient Email</dt>
              <dd className="mt-0.5 text-ink">{data.recipient_email ?? '—'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-steel">Note</dt>
              <dd className="mt-0.5 text-ink">{data.note ?? '—'}</dd>
            </div>
          </dl>
        </Card>

        <ApprovalPanel
          approval={data.approval}
          history={data.approval_history}
          decision={data.decision ?? null}
          kind="certificate"
          revoked={data.status === 'revoked'}
          busy={decide.isPending}
          error={
            decide.error ? ((decide.error as { message?: string }).message ?? 'Failed.') : null
          }
          onApprove={() => decide.mutate({ path: 'approve' })}
          onReject={(reason) => decide.mutate({ path: 'reject', reason })}
        />

        <div>
          <h2 className="mb-3 text-sm font-semibold text-ink">Redemption ledger</h2>
          <DataTable
            columns={columns}
            data={data.redemptions}
            keyExtractor={(r) => r._id}
            emptyMessage="Not used yet"
          />
        </div>
      </div>
    </div>
  );
}
